import { useTranslation } from 'react-i18next';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'clsx';

import { IconRcTick } from '@/assets';
import { IconRcStreamAskProArrow } from '@/assets/stream';
import { Button, UserAvatar, UserCardPopup, UserName } from '@/components';
import { useCurrentAddressInfo, useDeviceTypeIsMobile, useTheme } from '@/hooks';
import { FeedArticle } from '@/views/Stream';

import { ArticleEntityContentUrlEnabled } from '../Content';
import style from './index.module.scss';
import { openNewWindow } from '@/utils';

export const QaCard = ({
  article,
  isDetails,
  maxDisplay,
}: {
  article: FeedArticle;
  isDetails?: boolean;
  maxDisplay?: number;
}) => {
  const { t } = useTranslation();

  const { address } = useCurrentAddressInfo();
  const isMobile = useDeviceTypeIsMobile();
  const { isDark } = useTheme();
  const history = useHistory();

  const isResponed = !!article?.thread_count;

  const handleRelpyClick = useCallback(() => {
    history.push('/new-stream?tab=qa', { post: article });
  }, [article, history]);

  const isAnswer = article.pay_qa?.answerer?.id === address;
  const isActive = !article.pay_qa?._isExpired;
  const showResponseBtn = isDetails && !isResponed && isAnswer;

  const openAnswer = useCallback(() => {
    openNewWindow(`/profile/${article.pay_qa?.answerer?.id}/stream`);
  }, [article.pay_qa?.answerer?.id]);

  const openQuestioner = useCallback(() => {
    openNewWindow(`/profile/${article.pay_qa?.questioner?.id}/stream`);
  }, [article.pay_qa?.questioner?.id]);

  return (
    <div
      className={cx(
        style.container,
        isActive && style.isActive,
        isDark && style.isDark,
        isMobile && style.isMobile,
      )}>
      <div className={style.title}>
        <div className={style.creator}>AskPro</div>
        <div className={style.status}>
          {isResponed ? (
            <>
              <IconRcTick className={style.tickIcon} />
              {t('QaCard.1ru6bct')}
            </>
          ) : isActive ? (
            <div className={style.waitingWrap}>
              <span className={style.waitingStatus}>{t('QaCard.16j932g')}</span>
              <span className={style.endAt}>
                {t('QaCard.yos3f', { time: article.pay_qa?._endAt })}
              </span>
            </div>
          ) : (
            t('QaCard.no-answered')
          )}
        </div>
        <div className={cx(style.badge, !isResponed && style.isNoResponed)}>
          {t('QaCard.92hd1h')} {article.pay_qa?._offerPrice}
        </div>
      </div>
      <div className={style.body}>
        <div className={style.parties}>
          <div className={style.party}>
            <UserAvatar
              data={article.pay_qa?.questioner?.desc}
              size={isMobile ? 32 : 36}
              onClick={openQuestioner}
              userCardPopup
            />
            <div className={style.userInfo}>
              <UserCardPopup address={article.pay_qa?.questioner?.id!}>
                <UserName
                  name={article.pay_qa?.questioner?.web3_id}
                  address={article.pay_qa?.questioner?.id}
                  onClick={openQuestioner}
                  className={style.userName}
                  userDesc={article.pay_qa?.questioner?.desc}
                />
              </UserCardPopup>
              <div className={style.partyRole}>{t('QaCard.1p28jib')}</div>
            </div>
          </div>
          <IconRcStreamAskProArrow className={style.askproArrowIcon} />
          <div className={style.party}>
            <UserAvatar
              data={article.pay_qa?.answerer?.desc}
              size={isMobile ? 32 : 36}
              onClick={openAnswer}
              userCardPopup
            />
            <div className={style.userInfo}>
              <UserCardPopup address={article.pay_qa?.answerer?.id!}>
                <UserName
                  name={article.pay_qa?.answerer?.web3_id}
                  address={article.pay_qa?.answerer?.id}
                  onClick={openAnswer}
                  className={style.userName}
                  userDesc={article.pay_qa?.answerer?.desc}
                />
              </UserCardPopup>
              <div className={style.partyRole}>{t('QaCard.mhmyc6')}</div>
            </div>
          </div>
        </div>
      </div>
      <div className={style.contentWrap}>
        <div className={style.content}>
          <ArticleEntityContentUrlEnabled article={article} maxDisplay={maxDisplay} />
        </div>
      </div>
      {showResponseBtn ? (
        <div className={style.replyBtnWrap}>
          <Button onClick={handleRelpyClick} className={style.replyBtn} type="primary">
            {t('QaCard.upj68o')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
